import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AdminAuditEntryDto,
  AdminBotActionInput,
  AdminCleanupPreviewDto,
  AdminCleanupResultDto,
  AdminDeleteResultDto,
  AdminDemoCountsDto,
  AdminDemoResultDto,
  AdminOverviewDto,
  AdminSignOutResultDto,
  AdminUpdateUserInput,
  AdminUserDetailDto,
  AdminUserRowDto,
  AdminWalletsPageDto,
  AdminWalletsQuery,
  CursorPage,
} from '@ultrasnip/contracts';
import { api } from '@/lib/api-client';

/** Every admin cache entry sits under `admin`, so one invalidation refreshes the console. */
export const AdminKeys = {
  all: ['admin'] as const,
  users: (q: string) => ['admin', 'users', q] as const,
  user: (id: string) => ['admin', 'user', id] as const,
  lookup: (email: string) => ['admin', 'lookup', email] as const,
  audit: ['admin', 'audit'] as const,
  overview: (days: OverviewDays) => ['admin', 'overview', days] as const,
  wallets: (q: string, status: WalletStatus) => ['admin', 'wallets', status, q] as const,
};

/** The dashboard's chart periods (UTC days, today included). */
export const OVERVIEW_DAYS = [7, 30, 90] as const;
export type OverviewDays = (typeof OVERVIEW_DAYS)[number];
export type WalletStatus = AdminWalletsQuery['status'];

const PAGE = 50;
/** Accounts per page: enough to scan, short enough that the panels below stay in reach. */
const USERS_PAGE = 25;

export function usersQuery(q: string) {
  return infiniteQueryOptions({
    queryKey: AdminKeys.users(q),
    queryFn: ({ signal, pageParam }) =>
      api.get<CursorPage<AdminUserRowDto>>('/admin/users', {
        query: { limit: USERS_PAGE, q: q || undefined, cursor: pageParam ?? undefined },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // A new search keeps the current rows on screen until its results arrive.
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function userDetailQuery(id: string) {
  return queryOptions({
    queryKey: AdminKeys.user(id),
    queryFn: ({ signal }) => api.get<AdminUserDetailDto>(`/admin/users/${id}`, { signal }),
    staleTime: 10_000,
  });
}

/** The account with exactly this email, or null (searches, then matches the whole address). */
export function accountByEmailQuery(email: string) {
  const normalized = email.trim().toLowerCase();
  return queryOptions({
    queryKey: AdminKeys.lookup(normalized),
    queryFn: async ({ signal }) => {
      const page = await api.get<CursorPage<AdminUserRowDto>>('/admin/users', {
        query: { q: normalized, limit: 20 },
        signal,
      });
      return page.items.find((user) => user.email === normalized) ?? null;
    },
    enabled: normalized.includes('@'),
    staleTime: 30_000,
  });
}

/** The dashboard: platform figures plus `days` UTC days of volume (the API caches it for ~15 s). */
export function overviewQuery(days: OverviewDays) {
  return queryOptions({
    queryKey: AdminKeys.overview(days),
    queryFn: ({ signal }) => api.get<AdminOverviewDto>('/admin/overview', { query: { days }, signal }),
    // Another period keeps the current figures on screen until its numbers arrive.
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

/** Wallets per page: balances are read from the chain for each page, so keep it moderate. */
const WALLETS_PAGE = 50;

export function walletsQuery(q: string, status: WalletStatus) {
  return infiniteQueryOptions({
    queryKey: AdminKeys.wallets(q, status),
    queryFn: ({ signal, pageParam }) =>
      api.get<AdminWalletsPageDto>('/admin/wallets', {
        query: { q: q || undefined, status, limit: WALLETS_PAGE, cursor: pageParam ?? undefined },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export const auditQuery = infiniteQueryOptions({
  queryKey: AdminKeys.audit,
  queryFn: ({ signal, pageParam }) =>
    api.get<CursorPage<AdminAuditEntryDto>>('/admin/audit', {
      query: { limit: PAGE, cursor: pageParam ?? undefined },
      signal,
    }),
  initialPageParam: null as string | null,
  getNextPageParam: (last) => last.nextCursor,
  staleTime: 15_000,
});

/** Mutations on one account; each refreshes the whole console (lists, the sheet, the audit log). */
export function useAdminMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: AdminKeys.all });
  return {
    update: useMutation({
      mutationFn: ({ id, input }: { id: string; input: AdminUpdateUserInput }) =>
        api.patch<AdminUserRowDto>(`/admin/users/${id}`, input),
      onSettled: refresh,
    }),
    signOut: useMutation({
      mutationFn: (id: string) => api.post<AdminSignOutResultDto>(`/admin/users/${id}/sign-out`),
      onSettled: refresh,
    }),
    bot: useMutation({
      mutationFn: ({ id, input }: { id: string; input: AdminBotActionInput }) =>
        api.post<AdminUserRowDto>(`/admin/users/${id}/bot`, input),
      onSettled: refresh,
    }),
    loadDemo: useMutation({
      mutationFn: ({ id, sourceUserId }: { id: string; sourceUserId: string }) =>
        api.post<AdminDemoResultDto>(`/admin/users/${id}/demo-data`, { sourceUserId }),
      onSettled: refresh,
    }),
    removeDemo: useMutation({
      mutationFn: (id: string) => api.delete<AdminDemoCountsDto>(`/admin/users/${id}/demo-data`),
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: ({ id, confirmEmail }: { id: string; confirmEmail: string }) =>
        api.delete<AdminDeleteResultDto>(`/admin/users/${id}`, { confirmEmail }),
      onSuccess: (_, { id }) => queryClient.removeQueries({ queryKey: AdminKeys.user(id) }),
      onSettled: refresh,
    }),
  };
}

export function useCleanup() {
  const queryClient = useQueryClient();
  return {
    preview: useMutation({
      mutationFn: (emailSuffix: string) =>
        api.post<AdminCleanupPreviewDto>('/admin/cleanup/preview', { emailSuffix }),
    }),
    run: useMutation({
      mutationFn: (emailSuffix: string) =>
        api.post<AdminCleanupResultDto>('/admin/cleanup', { emailSuffix, confirm: 'DELETE' }),
      onSettled: () => queryClient.invalidateQueries({ queryKey: AdminKeys.all }),
    }),
  };
}
